import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, X, ArrowRight, Sparkles } from 'lucide-react';
import MarketingLayout from '@/components/MarketingLayout';

export default function PricingPage() {
  const { t, i18n } = useTranslation();

  const plans = [
    {
      name: t('pricing.plans.free.name'),
      price: '€0',
      period: t('pricing.plans.free.period'),
      description: t('pricing.plans.free.description'),
      features: [
        { text: i18n.language === 'en' ? '5 time entries/month' : '5 urenregistraties/maand', included: true },
        { text: i18n.language === 'en' ? '5 mileage entries/month' : '5 km registraties/maand', included: true },
        { text: i18n.language === 'en' ? '1 client' : '1 klant', included: true },
        { text: i18n.language === 'en' ? '1 invoice/month' : '1 factuur/maand', included: true },
        { text: 'PDF export', included: true },
        { text: 'KM Calculator', included: true },
        { text: i18n.language === 'en' ? 'Projects' : 'Projecten', included: false },
        { text: i18n.language === 'en' ? 'Email sending' : 'E-mail versturen', included: false },
        { text: i18n.language === 'en' ? 'Dark mode' : 'Donkere modus', included: false },
        { text: i18n.language === 'en' ? 'Client portal' : 'Klant portaal', included: false }
      ],
      cta: t('pricing.plans.free.cta'),
      popular: false,
      gradient: 'from-slate-500 to-slate-600'
    },
    {
      name: t('pricing.plans.basic.name'),
      price: '€4,99',
      period: t('pricing.plans.basic.period'),
      description: t('pricing.plans.basic.description'),
      features: [
        { text: i18n.language === 'en' ? '20 time entries/month' : '20 urenregistraties/maand', included: true },
        { text: i18n.language === 'en' ? '20 mileage entries/month' : '20 km registraties/maand', included: true },
        { text: i18n.language === 'en' ? '3 clients' : '3 klanten', included: true },
        { text: i18n.language === 'en' ? '4 invoices/month' : '4 facturen/maand', included: true },
        { text: i18n.language === 'en' ? '2 projects' : '2 projecten', included: true },
        { text: 'PDF + CSV export', included: true },
        { text: i18n.language === 'en' ? 'Email sending' : 'E-mail versturen', included: true },
        { text: i18n.language === 'en' ? 'Dark mode' : 'Donkere modus', included: true },
        { text: i18n.language === 'en' ? '2 templates' : '2 templates', included: true },
        { text: i18n.language === 'en' ? 'Credit notes' : 'Creditnota\'s', included: false },
        { text: i18n.language === 'en' ? 'Client portal' : 'Klant portaal', included: false }
      ],
      cta: t('pricing.plans.basic.cta'),
      popular: false,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      name: t('pricing.plans.pro.name'),
      price: '€9,99',
      period: t('pricing.plans.pro.period'),
      description: t('pricing.plans.pro.description'),
      features: [
        { text: i18n.language === 'en' ? 'Unlimited time entries' : 'Onbeperkt urenregistraties', included: true },
        { text: i18n.language === 'en' ? 'Unlimited mileage entries' : 'Onbeperkt km registraties', included: true },
        { text: i18n.language === 'en' ? 'Unlimited clients' : 'Onbeperkt klanten', included: true },
        { text: i18n.language === 'en' ? 'Unlimited invoices' : 'Onbeperkt facturen', included: true },
        { text: i18n.language === 'en' ? 'Unlimited projects' : 'Onbeperkt projecten', included: true },
        { text: i18n.language === 'en' ? 'All exports (PDF, CSV, Excel, BTW)' : 'Alle exports (PDF, CSV, Excel, BTW)', included: true },
        { text: i18n.language === 'en' ? 'All 3 templates' : 'Alle 3 templates', included: true },
        { text: i18n.language === 'en' ? 'Credit notes' : 'Creditnota\'s', included: true },
        { text: i18n.language === 'en' ? 'Auto reminders' : 'Automatische herinneringen', included: true },
        { text: i18n.language === 'en' ? 'Client portal' : 'Klant portaal', included: true },
        { text: i18n.language === 'en' ? 'Dark mode' : 'Donkere modus', included: true }
      ],
      cta: t('pricing.plans.pro.cta'),
      popular: true,
      gradient: 'from-cyan-500 to-teal-500'
    }
  ];

  const faqs = [
    {
      q: t('pricing.faq.trial.q'),
      a: t('pricing.faq.trial.a')
    },
    {
      q: t('pricing.faq.cancel.q'),
      a: t('pricing.faq.cancel.a')
    },
    {
      q: t('pricing.faq.payment.q'),
      a: t('pricing.faq.payment.a')
    },
    {
      q: t('pricing.faq.upgrade.q'),
      a: t('pricing.faq.upgrade.a')
    }
  ];

  return (
    <MarketingLayout
      title={t('pricing.meta.title')}
      description={t('pricing.meta.description')}
      keywords="urenbuddy prijzen, zzp software kosten, freelancer administratie prijs, facturatie abonnement"
    >
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-20 pb-24">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-300">{t('pricing.hero.badge')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('pricing.hero.title')}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('pricing.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={`relative border-0 overflow-hidden ${
                  plan.popular ? 'ring-2 ring-cyan-500 shadow-xl scale-105' : 'shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-center py-2 text-sm font-medium">
                    {t('pricing.mostPopular')}
                  </div>
                )}
                <CardHeader className={`${plan.popular ? 'pt-12' : 'pt-6'}`}>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-slate-500 ml-2">{plan.period}</span>
                  </div>
                  <p className="text-slate-600 mt-2">{plan.description}</p>
                </CardHeader>
                <CardContent className="pb-8">
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        {feature.included ? (
                          <Check className="w-5 h-5 text-teal-500" />
                        ) : (
                          <X className="w-5 h-5 text-slate-300" />
                        )}
                        <span className={feature.included ? 'text-slate-700' : 'text-slate-400'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    asChild
                    className={`w-full ${
                      plan.popular
                        ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600'
                        : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                  >
                    <Link to="/register">
                      {plan.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-8">
            {t('pricing.vatNote')}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            {t('pricing.faq.title')}
          </h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-cyan-500 to-teal-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            {t('pricing.cta.title')}
          </h2>
          <p className="text-xl text-cyan-100 mb-10">
            {t('pricing.cta.subtitle')}
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-cyan-600 hover:bg-cyan-50 px-10 py-6 text-lg rounded-xl"
          >
            <Link to="/register">
              {t('pricing.cta.button')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </MarketingLayout>
  );
}
